/**
 * Largest file the upload chain accepts. It mirrors the server side limit (PHP
 * upload_max_filesize on the BO proxy): keep both in sync.
 */
export const MAX_UPLOAD_SIZE = 4 * 1024 * 1024;

/** Longest side, in pixels, an uploaded image is scaled down to. Plenty to read a document. */
const MAX_IMAGE_DIMENSION = 2000;
const JPEG_QUALITY = 0.8;
/** Below this size an image is sent as is: re-encoding would gain nothing worth the quality loss. */
const COMPRESSION_THRESHOLD = 300 * 1024;
/** Formats a canvas can redraw without losing anything that matters (a GIF would lose its animation). */
const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/png', 'image/bmp', 'image/webp'];

function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error(`Cannot decode image ${file.name}`));
        };
        image.src = url;
    });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Scales an image down and re-encodes it as JPEG so that a phone photo of a document
 * fits the upload limit. Anything else (PDF, GIF, small images), or an image that would
 * not get smaller, is returned untouched; so is the original if the browser fails to decode it.
 */
export async function compressImage(file: File): Promise<File> {
    if (!COMPRESSIBLE_TYPES.includes(file.type) || file.size <= COMPRESSION_THRESHOLD) {
        return file;
    }
    try {
        const image = await loadImage(file);
        const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.naturalWidth * scale);
        canvas.height = Math.round(image.naturalHeight * scale);
        const context = canvas.getContext('2d');
        if (!context) {
            return file;
        }
        // JPEG has no transparency: without a background, transparent PNG areas turn black
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        const blob = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
        if (!blob || blob.size >= file.size) {
            return file;
        }
        const name = file.name.replace(/\.[^.]*$/, '') + '.jpg';
        return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
    } catch (e) {
        return file;
    }
}

/**
 * Compresses the file held by `field` in place.
 * @return false when the file is still over MAX_UPLOAD_SIZE and must not be sent
 */
export async function prepareUploadFile(formData: FormData, field: string): Promise<boolean> {
    const file = formData.get(field);
    if (!(file instanceof File) || !file.size) {
        return true;
    }
    const compressed = await compressImage(file);
    if (compressed !== file) {
        formData.set(field, compressed, compressed.name);
    }
    return compressed.size <= MAX_UPLOAD_SIZE;
}
